import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./api/queryClient";
import { AuthProvider } from "./auth/AuthProvider";
import { ViewContextProvider } from "./state/ViewContext";
import AppLayout from "./layout/AppLayout";

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Sessions = lazy(() => import("./pages/Sessions"));
const SessionDrawer = lazy(() => import("./pages/SessionDrawer"));
const Agents = lazy(() => import("./pages/Agents"));
const AgentDrawer = lazy(() => import("./pages/AgentDrawer"));
const Catalog = lazy(() => import("./pages/Catalog"));
const DataSources = lazy(() => import("./pages/DataSources"));
const UsersPage = lazy(() => import("./pages/Users"));
const Tools = lazy(() => import("./pages/Tools"));
const Projects = lazy(() => import("./pages/Projects"));
const Messages = lazy(() => import("./pages/Messages"));
const MessageDrawer = lazy(() => import("./pages/MessageDrawer"));
const Office = lazy(() => import("./pages/Office"));
const OfficeTraceDrawer = lazy(() => import("./pages/OfficeTraceDrawer"));
const Mike = lazy(() => import("./pages/Mike"));
const MikeMatterDrawer = lazy(() => import("./pages/MikeMatterDrawer"));
const MikeChatDrawer = lazy(() => import("./pages/MikeChatDrawer"));
const Account = lazy(() => import("./pages/Account"));
const AdminPolicy = lazy(() => import("./pages/AdminPolicy"));

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <ViewContextProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<Suspense fallback={<PageSkeleton />}><Dashboard /></Suspense>} />
            <Route path="/sessions" element={<Suspense fallback={<PageSkeleton />}><Sessions /></Suspense>}>
              {/* Nested route — the drawer renders inside Sessions via <Outlet /> */}
              <Route path=":sessionId" element={<Suspense fallback={null}><SessionDrawer /></Suspense>} />
            </Route>
            <Route path="/agents" element={<Suspense fallback={<PageSkeleton />}><Agents /></Suspense>}>
              <Route path=":runUuid" element={<Suspense fallback={null}><AgentDrawer /></Suspense>} />
            </Route>
            <Route path="/catalog" element={<Suspense fallback={<PageSkeleton />}><Catalog /></Suspense>} />
            <Route path="/datasources" element={<Suspense fallback={<PageSkeleton />}><DataSources /></Suspense>} />
            <Route path="/users" element={<Suspense fallback={<PageSkeleton />}><UsersPage /></Suspense>} />
            <Route path="/tools" element={<Suspense fallback={<PageSkeleton />}><Tools /></Suspense>} />
            <Route path="/projects" element={<Suspense fallback={<PageSkeleton />}><Projects /></Suspense>} />
            <Route path="/compliance" element={<Suspense fallback={<PageSkeleton />}><Messages /></Suspense>}>
              <Route path=":chatId" element={<Suspense fallback={null}><MessageDrawer /></Suspense>} />
            </Route>
            <Route path="/office" element={<Suspense fallback={<PageSkeleton />}><Office /></Suspense>}>
              <Route path=":traceId" element={<Suspense fallback={null}><OfficeTraceDrawer /></Suspense>} />
            </Route>
            <Route path="/account" element={<Suspense fallback={<PageSkeleton />}><Account /></Suspense>} />
            <Route path="/admin/policy" element={<Suspense fallback={<PageSkeleton />}><AdminPolicy /></Suspense>} />
            <Route path="/mike" element={<Suspense fallback={<PageSkeleton />}><Mike /></Suspense>}>
              <Route path=":projectId" element={<Suspense fallback={null}><MikeMatterDrawer /></Suspense>}>
                {/* Nested under :projectId so the matter drawer stays
                    mounted (and visible) behind the chat drawer. */}
                <Route path="chats/:chatId" element={<Suspense fallback={null}><MikeChatDrawer /></Suspense>} />
              </Route>
            </Route>
          </Route>

          <Route element={<AppLayout />}>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </ViewContextProvider>
      </AuthProvider>

      {/* Devtools only show in dev — the import itself is tree-shaken
          out of production builds by Vite. */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

function PageSkeleton() {
  return (
    <div>
      <div className="mb-6">
        <div className="h-7 w-40 rounded bg-bg-surface-2" />
        <div className="mt-2 h-4 w-72 rounded bg-bg-surface-2" />
      </div>
      <div className="h-32 rounded-[10px] border border-border-subtle bg-bg-surface" />
    </div>
  );
}

function NotFound() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-fg-strong tracking-tight">404</h1>
        <p className="text-[13px] text-fg-muted mt-1">No page matches this URL.</p>
      </div>
    </div>
  );
}

export default App;
